// app/learn/creator/edit/[worldId].tsx
// =============================================================================
// V2 World Editor — full editing of live_practice_v1 worlds
// Tabs: Details | Lessons | Scenarios | Settings
// =============================================================================

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  TextInput,
  StyleSheet,
  Platform,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { ArrowRight, Copy, ExternalLink, CircleCheck as CheckCircle } from 'lucide-react-native';
import { supabase } from '@/utils/supabase';
import { theme } from '@/constants/theme';
import {
  COURSE_PALETTE,
  isValidHexColor,
  buildShareUrl,
  LANGUAGES,
  ensureKursiPlayOpenAccessSession,
} from '@/utils/kursiPlay';
import { publishAiCourse } from '@/utils/kursiPlayCreatorService';
import type {
  KpScenarioTemplateType,
  KpQualityTier,
  KpScenario,
  KpScenarioAction,
  KpScenarioConsequence,
} from '@/types/kursi-play-v2/index';

// ── Types ─────────────────────────────────────────────────────────────────────

type WorldMeta = {
  id: string;
  slug: string;
  world_name: string;
  description: string | null;
  learning_goal: string | null;
  language: string;
  status: 'draft' | 'generating' | 'published' | 'archived';
  primary_color: string;
  character_name: string | null;
  character_role: string | null;
  narrative_intro: string | null;
  created_at: string;
  published_at: string | null;
};

type Lesson = {
  id: string;
  world_id: string;
  title: string;
  description: string | null;
  order_index: number;
  estimated_minutes: number;
  xp_reward: number;
};

type PublishGateCheck = {
  id: string;
  title: string;
  detail: string;
  passed: boolean;
};

type ScenarioWithActions = KpScenario & {
  actions: (KpScenarioAction & { consequence: KpScenarioConsequence | null })[];
};

type Tab = 'details' | 'lessons' | 'scenarios' | 'analytics' | 'settings';

const BG = '#0F0F1A';
const SURFACE = '#1A1830';
const SURFACE2 = '#201E3A';
const BORDER = '#2D2B50';
const TEXT = '#F0EFF8';
const TEXT_MED = '#A09CC0';
const PRIMARY = theme.colors.primary;
const GREEN = '#10B981';
const GOLD = '#F59E0B';
const RED = '#EF4444';
const ORANGE = '#F97316';

const QUALITY_CONFIG: Record<KpQualityTier, { label: string; color: string; bg: string }> = {
  strong:     { label: 'חזק',     color: GREEN,  bg: `${GREEN}18`  },
  acceptable: { label: 'סביר',    color: GOLD,   bg: `${GOLD}18`   },
  weak:       { label: 'חלש',     color: ORANGE, bg: `${ORANGE}18` },
  risky:      { label: 'מסוכן',   color: RED,    bg: `${RED}18`    },
};

const TEMPLATE_LABELS: Record<KpScenarioTemplateType, string> = {
  decision:     'החלטה',
  conversation: 'שיחה',
  analysis:     'ניתוח',
  fix:          'תיקון',
};

// ── Main ──────────────────────────────────────────────────────────────────────

export default function EditV2World() {
  const { worldId } = useLocalSearchParams<{ worldId: string }>();
  const [tab, setTab] = useState<Tab>('details');
  const [world, setWorld] = useState<WorldMeta | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string, ok = true) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, ok });
    toastTimer.current = setTimeout(() => setToast(null), 2800);
  }, []);

  const load = useCallback(async () => {
    if (!worldId) return;
    setLoading(true);
    await ensureKursiPlayOpenAccessSession(supabase);

    const [worldRes, lessonRes] = await Promise.all([
      supabase
        .from('kp_worlds')
        .select('id,slug,world_name,description,learning_goal,language,status,primary_color,character_name,character_role,narrative_intro,created_at,published_at')
        .eq('id', worldId)
        .maybeSingle(),
      supabase
        .from('kp_lessons')
        .select('id,world_id,title,description,order_index,estimated_minutes,xp_reward')
        .eq('world_id', worldId)
        .order('order_index'),
    ]);

    if (worldRes.data) setWorld(worldRes.data as WorldMeta);
    if (lessonRes.data) setLessons(lessonRes.data as Lesson[]);
    setLoading(false);
  }, [worldId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <View style={s.center}>
        <ActivityIndicator color={PRIMARY} size="large" />
        <Text style={s.loadingText}>טוען...</Text>
      </View>
    );
  }

  if (!world) {
    return (
      <View style={s.center}>
        <Text style={s.errorText}>המסלול לא נמצא</Text>
        <Pressable
          onPress={() => {
            if (router.canGoBack()) router.back();
            else router.replace('/learn/creator' as any);
          }}
          style={s.backBtn}
        >
          <Text style={s.backBtnText}>חזור לדשבורד</Text>
        </Pressable>
      </View>
    );
  }

  const isPublished = world.status === 'published';

  return (
    <View style={s.root}>
      {/* Top bar */}
      <View style={s.topBar}>
        <Pressable onPress={() => router.back()} style={s.backIcon} accessibilityRole="button">
          <ArrowRight color={TEXT_MED} size={20} />
        </Pressable>
        <View style={{ flex: 1 }}>
          <Text style={s.topTitle} numberOfLines={1}>{world.world_name}</Text>
          <Text style={s.topSub}>
            {isPublished ? 'Published · גלוי ללומדים' : world.status === 'archived' ? 'Hidden · מוסתר מלומדים' : 'Draft · טיוטה'}
          </Text>
        </View>
        <View style={[s.v2Badge, { backgroundColor: `${PRIMARY}22` }]}>
          <Text style={[s.v2BadgeText, { color: PRIMARY }]}>KURSI LEARN</Text>
        </View>
      </View>

      {/* Tab bar */}
      <View style={s.tabBar}>
        {(['details', 'lessons', 'scenarios', 'analytics', 'settings'] as Tab[]).map((t) => (
          <Pressable key={t} style={[s.tabBtn, tab === t && s.tabBtnActive]} onPress={() => setTab(t)}>
            <Text style={[s.tabBtnText, tab === t && s.tabBtnTextActive]}>
              {t === 'details' ? 'פרטים'
                : t === 'lessons' ? 'שיעורים'
                : t === 'scenarios' ? 'תרחישים'
                : t === 'analytics' ? 'ניתוח'
                : 'הגדרות'}
            </Text>
          </Pressable>
        ))}
      </View>

      {/* Content */}
      <ScrollView style={s.scroll} contentContainerStyle={s.scrollContent} keyboardShouldPersistTaps="handled">
        {tab === 'details' && (
          <DetailsTab world={world} onSave={async (patch) => {
            const { error } = await supabase.from('kp_worlds').update(patch).eq('id', world.id);
            if (error) { showToast('שגיאה בשמירה', false); return; }
            setWorld((prev) => prev ? { ...prev, ...patch } : prev);
            showToast('נשמר');
          }} />
        )}
        {tab === 'lessons' && (
          <LessonsTab lessons={lessons} onSaveLesson={async (lessonId, patch) => {
            const { error } = await supabase.from('kp_lessons').update(patch).eq('id', lessonId);
            if (error) { showToast('שגיאה בשמירה', false); return; }
            setLessons((prev) => prev.map((l) => l.id === lessonId ? { ...l, ...patch } : l));
            showToast('שיעור נשמר');
          }} />
        )}
        {tab === 'scenarios' && (
          <ScenariosTab worldId={world.id} lessons={lessons} showToast={showToast} />
        )}
        {tab === 'analytics' && (
          <AnalyticsTab worldId={world.id} lessons={lessons} />
        )}
        {tab === 'settings' && (
          <SettingsTab world={world} lessons={lessons} onPublish={async () => {
            try {
              await publishAiCourse(world.id);
              setWorld((prev) => prev ? { ...prev, status: 'published' } : prev);
              showToast('פורסם בהצלחה!');
            } catch {
              showToast('שגיאה בפרסום', false);
            }
          }} onArchive={async () => {
            const { error } = await supabase.from('kp_worlds').update({ status: 'archived' }).eq('id', world.id);
            if (error) { showToast('שגיאה בארכיב', false); return; }
            setWorld((prev) => prev ? { ...prev, status: 'archived' } : prev);
            showToast('הועבר לארכיב');
          }} />
        )}
      </ScrollView>

      {/* Toast */}
      {toast && (
        <View style={[s.toast, { backgroundColor: toast.ok ? `${GREEN}EE` : `${RED}EE` }]}>
          <Text style={s.toastText}>{toast.msg}</Text>
        </View>
      )}
    </View>
  );
}

// ── DetailsTab ────────────────────────────────────────────────────────────────

function DetailsTab({
  world,
  onSave,
}: {
  world: WorldMeta;
  onSave: (patch: Partial<WorldMeta>) => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    world_name:      world.world_name,
    description:     world.description ?? '',
    learning_goal:   world.learning_goal ?? '',
    character_name:  world.character_name ?? '',
    character_role:  world.character_role ?? '',
    narrative_intro: world.narrative_intro ?? '',
    language:        world.language,
    primary_color:   world.primary_color,
  });

  const [copied, setCopied] = useState(false);
  const isPublished = world.status === 'published';

  const save = async () => {
    setSaving(true);
    await onSave({
      world_name:      draft.world_name.trim() || world.world_name,
      description:     draft.description.trim() || null,
      learning_goal:   draft.learning_goal.trim() || null,
      character_name:  draft.character_name.trim() || null,
      character_role:  draft.character_role.trim() || null,
      narrative_intro: draft.narrative_intro.trim() || null,
      language:        draft.language,
      primary_color:   draft.primary_color,
    });
    setSaving(false);
  };

  const copyLink = async () => {
    if (Platform.OS !== 'web') return;
    try {
      await navigator.clipboard.writeText(buildShareUrl(world.slug));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <View style={s.section}>
      <Text style={s.sectionTitle}>פרטי המסלול</Text>

      <FieldGroup label="שם המסלול">
        <TextInput style={s.input} value={draft.world_name}
          onChangeText={(v) => setDraft((d) => ({ ...d, world_name: v }))}
          placeholderTextColor={TEXT_MED} textAlign="right" />
      </FieldGroup>

      <FieldGroup label="תיאור קצר">
        <TextInput style={[s.input, s.inputMulti]} value={draft.description}
          onChangeText={(v) => setDraft((d) => ({ ...d, description: v }))}
          multiline numberOfLines={3} placeholderTextColor={TEXT_MED} textAlign="right"
          placeholder="תיאור שמופיע ללומדים בעמוד הבית" />
      </FieldGroup>

      <FieldGroup label="מטרת הלמידה">
        <TextInput style={[s.input, s.inputMulti]} value={draft.learning_goal}
          onChangeText={(v) => setDraft((d) => ({ ...d, learning_goal: v }))}
          multiline numberOfLines={3} placeholderTextColor={TEXT_MED} textAlign="right"
          placeholder="מה הלומד ידע / יוכל לעשות אחרי המסלול?" />
      </FieldGroup>

      <Text style={s.subSectionTitle}>דמות מלווה</Text>

      <FieldGroup label="שם הדמות">
        <TextInput style={s.input} value={draft.character_name}
          onChangeText={(v) => setDraft((d) => ({ ...d, character_name: v }))}
          placeholderTextColor={TEXT_MED} textAlign="right"
          placeholder='לדוגמה: "אבי" או "מנהל המחלקה"' />
      </FieldGroup>

      <FieldGroup label="תפקיד הדמות">
        <TextInput style={s.input} value={draft.character_role}
          onChangeText={(v) => setDraft((d) => ({ ...d, character_role: v }))}
          placeholderTextColor={TEXT_MED} textAlign="right"
          placeholder='לדוגמה: "מנטור בכיר" או "לקוח"' />
      </FieldGroup>

      <FieldGroup label="נרטיב פתיחה">
        <TextInput style={[s.input, s.inputMulti]} value={draft.narrative_intro}
          onChangeText={(v) => setDraft((d) => ({ ...d, narrative_intro: v }))}
          multiline numberOfLines={4} placeholderTextColor={TEXT_MED} textAlign="right"
          placeholder="מבוא שמוצג ללומד לפני תחילת המסלול" />
      </FieldGroup>

      <Text style={s.subSectionTitle}>שפה ועיצוב</Text>

      <FieldGroup label="שפת המסלול">
        <View style={s.langRow}>
          {LANGUAGES.map((l) => (
            <Pressable key={l.value} style={[s.langChip, draft.language === l.value && { borderColor: PRIMARY, backgroundColor: `${PRIMARY}22` }]}
              onPress={() => setDraft((d) => ({ ...d, language: l.value }))}>
              <Text style={s.langChipText}>{l.flag} {l.label}</Text>
            </Pressable>
          ))}
        </View>
      </FieldGroup>

      <FieldGroup label="צבע ראשי">
        <View style={s.colorRow}>
          {COURSE_PALETTE.map((c) => (
            <Pressable key={c} style={[s.colorSwatch, { backgroundColor: c },
              draft.primary_color === c && s.colorSwatchActive]}
              onPress={() => setDraft((d) => ({ ...d, primary_color: c }))} />
          ))}
        </View>
        <TextInput style={[s.input, { marginTop: 8 }]} value={draft.primary_color}
          onChangeText={(v) => {
            if (isValidHexColor(v)) setDraft((d) => ({ ...d, primary_color: v }));
          }}
          placeholder="#7C3AED" placeholderTextColor={TEXT_MED}
          maxLength={7} autoCapitalize="characters" />
      </FieldGroup>

      {isPublished && (
        <Pressable style={[s.copyLinkBtn, copied && { borderColor: GREEN }]} onPress={copyLink}>
          {copied ? <CheckCircle color={GREEN} size={15} /> : <Copy color={TEXT_MED} size={15} />}
          <Text style={[s.copyLinkText, copied && { color: GREEN }]}>
            {copied ? 'קישור הועתק!' : 'העתק קישור שיתוף'}
          </Text>
        </Pressable>
      )}

      <Pressable style={s.saveBtn} onPress={save} disabled={saving} accessibilityRole="button">
        {saving ? <ActivityIndicator size="small" color="#fff" /> : <Text style={s.saveBtnText}>שמור שינויים</Text>}
      </Pressable>
    </View>
  );
}

// ── LessonsTab ────────────────────────────────────────────────────────────────

function LessonsTab({
  lessons,
  onSaveLesson,
}: {
  lessons: Lesson[];
  onSaveLesson: (id: string, patch: Partial<Lesson>) => Promise<void>;
}) {
  if (lessons.length === 0) {
    return (
      <View style={s.section}>
        <Text style={s.emptyText}>אין שיעורים במסלול הזה עדיין.</Text>
      </View>
    );
  }

  return (
    <View style={s.section}>
      <Text style={s.sectionTitle}>שיעורים ({lessons.length})</Text>
      {lessons.map((lesson) => (
        <LessonCard key={lesson.id} lesson={lesson} onSave={(patch) => onSaveLesson(lesson.id, patch)} />
      ))}
    </View>
  );
}

function LessonCard({ lesson, onSave }: { lesson: Lesson; onSave: (patch: Partial<Lesson>) => Promise<void> }) {
  const [title, setTitle] = useState(lesson.title);
  const [description, setDescription] = useState(lesson.description ?? '');
  const [xpReward, setXpReward] = useState(String(lesson.xp_reward));
  const [minutes, setMinutes] = useState(String(lesson.estimated_minutes));
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const save = async () => {
    setSaving(true);
    await onSave({
      title: title.trim() || lesson.title,
      description: description.trim() || null,
      xp_reward: parseInt(xpReward) || lesson.xp_reward,
      estimated_minutes: parseInt(minutes) || lesson.estimated_minutes,
    });
    setSaving(false);
  };

  return (
    <View style={s.lessonCard}>
      <Pressable style={s.lessonCardHeader} onPress={() => setExpanded((e) => !e)}>
        <View style={s.lessonOrderBadge}>
          <Text style={s.lessonOrderText}>{lesson.order_index + 1}</Text>
        </View>
        <Text style={s.lessonCardTitle} numberOfLines={1}>{lesson.title}</Text>
        <Text style={s.lessonChevron}>{expanded ? '▲' : '▼'}</Text>
      </Pressable>

      {expanded && (
        <View style={s.lessonCardBody}>
          <FieldGroup label="כותרת שיעור">
            <TextInput style={s.input} value={title}
              onChangeText={setTitle} placeholderTextColor={TEXT_MED} textAlign="right" />
          </FieldGroup>
          <FieldGroup label="תיאור">
            <TextInput style={[s.input, s.inputMulti]} value={description}
              onChangeText={setDescription} multiline numberOfLines={2}
              placeholderTextColor={TEXT_MED} textAlign="right" />
          </FieldGroup>
          <View style={s.row}>
            <FieldGroup label="XP" style={{ flex: 1 }}>
              <TextInput style={s.input} value={xpReward} onChangeText={setXpReward}
                keyboardType="number-pad" placeholderTextColor={TEXT_MED} textAlign="right" />
            </FieldGroup>
            <FieldGroup label="דקות" style={{ flex: 1 }}>
              <TextInput style={s.input} value={minutes} onChangeText={setMinutes}
                keyboardType="number-pad" placeholderTextColor={TEXT_MED} textAlign="right" />
            </FieldGroup>
          </View>
          <Pressable style={s.saveBtnSmall} onPress={save} disabled={saving}>
            {saving ? <ActivityIndicator size="small" color="#fff" /> : <Text style={s.saveBtnText}>שמור שיעור</Text>}
          </Pressable>
        </View>
      )}
    </View>
  );
}

// ── ScenariosTab ──────────────────────────────────────────────────────────────

function ScenariosTab({
  worldId,
  lessons,
  showToast,
}: {
  worldId: string;
  lessons: Lesson[];
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(lessons[0]?.id ?? null);
  const [scenarios, setScenarios] = useState<ScenarioWithActions[]>([]);
  const [loadingScenarios, setLoadingScenarios] = useState(false);

  const loadScenarios = useCallback(async (lessonId: string) => {
    setLoadingScenarios(true);
    const { data: scenarioRows } = await supabase
      .from('kp_scenarios')
      .select('*')
      .eq('lesson_id', lessonId)
      .eq('world_id', worldId)
      .order('order_index');

    if (!scenarioRows || scenarioRows.length === 0) {
      setScenarios([]);
      setLoadingScenarios(false);
      return;
    }

    const scenarioIds = scenarioRows.map((s: any) => s.id);

    const [{ data: actionRows }, { data: consequenceRows }] = await Promise.all([
      supabase.from('kp_scenario_actions').select('*').in('scenario_id', scenarioIds).order('order_index'),
      supabase.from('kp_scenario_consequences').select('*').in('action_id',
        (await supabase.from('kp_scenario_actions').select('id').in('scenario_id', scenarioIds)).data?.map((a: any) => a.id) ?? []
      ),
    ]);

    const consequenceByActionId: Record<string, KpScenarioConsequence> = {};
    (consequenceRows ?? []).forEach((c: any) => { consequenceByActionId[c.action_id] = c; });

    const actionsByScenarioId: Record<string, (KpScenarioAction & { consequence: KpScenarioConsequence | null })[]> = {};
    (actionRows ?? []).forEach((a: any) => {
      if (!actionsByScenarioId[a.scenario_id]) actionsByScenarioId[a.scenario_id] = [];
      actionsByScenarioId[a.scenario_id].push({ ...a, consequence: consequenceByActionId[a.id] ?? null });
    });

    setScenarios(scenarioRows.map((sc: any) => ({ ...sc, actions: actionsByScenarioId[sc.id] ?? [] })));
    setLoadingScenarios(false);
  }, [worldId]);

  useEffect(() => {
    if (selectedLessonId) loadScenarios(selectedLessonId);
  }, [selectedLessonId, loadScenarios]);

  const saveScenario = async (scenario: ScenarioWithActions) => {
    const { error: scErr } = await supabase.from('kp_scenarios').update({
      title:            scenario.title,
      situation:        scenario.situation,
      role_instruction: scenario.role_instruction,
      decision_point:   scenario.decision_point,
    }).eq('id', scenario.id);
    if (scErr) { showToast('שגיאה בשמירת תרחיש', false); return; }

    for (const action of scenario.actions) {
      await supabase.from('kp_scenario_actions').update({
        label:       action.label,
        description: action.description,
      }).eq('id', action.id);

      if (action.consequence) {
        await supabase.from('kp_scenario_consequences').update({
          narrative:     action.consequence.narrative,
          outcome_label: action.consequence.outcome_label,
        }).eq('id', action.consequence.id);
      }
    }
    showToast('תרחיש נשמר');
  };

  if (lessons.length === 0) {
    return <View style={s.section}><Text style={s.emptyText}>אין שיעורים — לא ניתן לערוך תרחישים.</Text></View>;
  }

  return (
    <View style={s.section}>
      <Text style={s.sectionTitle}>תרחישים</Text>

      {/* Lesson selector */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.lessonSelector} contentContainerStyle={{ gap: 8, paddingHorizontal: 2 }}>
        {lessons.map((l) => (
          <Pressable key={l.id} style={[s.lessonChip, selectedLessonId === l.id && s.lessonChipActive]}
            onPress={() => setSelectedLessonId(l.id)}>
            <Text style={[s.lessonChipText, selectedLessonId === l.id && s.lessonChipTextActive]} numberOfLines={1}>
              {l.order_index + 1}. {l.title}
            </Text>
          </Pressable>
        ))}
      </ScrollView>

      {loadingScenarios && <ActivityIndicator color={PRIMARY} style={{ marginTop: 24 }} />}

      {!loadingScenarios && scenarios.length === 0 && (
        <Text style={s.emptyText}>לשיעור זה אין תרחישים עדיין.</Text>
      )}

      {!loadingScenarios && scenarios.map((sc) => (
        <ScenarioCard key={sc.id} scenario={sc}
          onUpdate={(updated) => setScenarios((prev) => prev.map((s) => s.id === sc.id ? updated : s))}
          onSave={() => saveScenario(sc)} />
      ))}
    </View>
  );
}

function ScenarioCard({
  scenario,
  onUpdate,
  onSave,
}: {
  scenario: ScenarioWithActions;
  onUpdate: (s: ScenarioWithActions) => void;
  onSave: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const update = (patch: Partial<ScenarioWithActions>) => onUpdate({ ...scenario, ...patch });

  const handleSave = async () => {
    setSaving(true);
    await onSave();
    setSaving(false);
  };

  const templateLabel = TEMPLATE_LABELS[scenario.template_type] ?? scenario.template_type;

  return (
    <View style={s.scenarioCard}>
      <Pressable style={s.scenarioHeader} onPress={() => setExpanded((e) => !e)}>
        <View style={{ flex: 1 }}>
          <Text style={s.scenarioTitle} numberOfLines={1}>
            {scenario.order_index + 1}. {scenario.title}
          </Text>
          <Text style={s.scenarioMeta}>{templateLabel} · {scenario.xp_value} XP · {scenario.actions.length} פעולות</Text>
        </View>
        <Text style={s.lessonChevron}>{expanded ? '▲' : '▼'}</Text>
      </Pressable>

      {expanded && (
        <View style={s.scenarioBody}>
          <FieldGroup label="מצב / סיטואציה">
            <TextInput style={[s.input, s.inputMulti]} value={scenario.situation}
              onChangeText={(v) => update({ situation: v })}
              multiline numberOfLines={3} placeholderTextColor={TEXT_MED} textAlign="right" />
          </FieldGroup>

          <FieldGroup label="הוראת תפקיד">
            <TextInput style={[s.input, s.inputMulti]} value={scenario.role_instruction}
              onChangeText={(v) => update({ role_instruction: v })}
              multiline numberOfLines={2} placeholderTextColor={TEXT_MED} textAlign="right" />
          </FieldGroup>

          <FieldGroup label="נקודת ההחלטה">
            <TextInput style={[s.input, s.inputMulti]} value={scenario.decision_point}
              onChangeText={(v) => update({ decision_point: v })}
              multiline numberOfLines={2} placeholderTextColor={TEXT_MED} textAlign="right" />
          </FieldGroup>

          <Text style={s.subSectionTitle}>פעולות ({scenario.actions.length})</Text>

          {scenario.actions.map((action, idx) => {
            const qCfg = QUALITY_CONFIG[action.quality_tier];
            return (
              <View key={action.id} style={s.actionCard}>
                <View style={s.actionHeader}>
                  <Text style={s.actionIndex}>פעולה {idx + 1}</Text>
                  <View style={[s.qualityBadge, { backgroundColor: qCfg.bg }]}>
                    <Text style={[s.qualityBadgeText, { color: qCfg.color }]}>{qCfg.label}</Text>
                  </View>
                </View>

                <FieldGroup label="תיאור הפעולה">
                  <TextInput style={[s.input, s.inputMulti]} value={action.label}
                    onChangeText={(v) => update({
                      actions: scenario.actions.map((a) => a.id === action.id ? { ...a, label: v } : a),
                    })}
                    multiline numberOfLines={2} placeholderTextColor={TEXT_MED} textAlign="right" />
                </FieldGroup>

                {action.consequence && (
                  <>
                    <FieldGroup label="נרטיב תוצאה">
                      <TextInput style={[s.input, s.inputMulti]} value={action.consequence.narrative}
                        onChangeText={(v) => update({
                          actions: scenario.actions.map((a) => a.id === action.id
                            ? { ...a, consequence: { ...a.consequence!, narrative: v } } : a),
                        })}
                        multiline numberOfLines={3} placeholderTextColor={TEXT_MED} textAlign="right" />
                    </FieldGroup>
                    <FieldGroup label="תווית תוצאה">
                      <TextInput style={s.input} value={action.consequence.outcome_label}
                        onChangeText={(v) => update({
                          actions: scenario.actions.map((a) => a.id === action.id
                            ? { ...a, consequence: { ...a.consequence!, outcome_label: v } } : a),
                        })}
                        placeholderTextColor={TEXT_MED} textAlign="right" />
                    </FieldGroup>
                  </>
                )}
              </View>
            );
          })}

          <Pressable style={s.saveBtnSmall} onPress={handleSave} disabled={saving}>
            {saving ? <ActivityIndicator size="small" color="#fff" /> : <Text style={s.saveBtnText}>שמור תרחיש</Text>}
          </Pressable>
        </View>
      )}
    </View>
  );
}

// ── AnalyticsTab ──────────────────────────────────────────────────────────────

type ScenarioStats = {
  scenario_id:    string;
  scenario_title: string;
  lesson_title:   string;
  total_attempts: number;
  strong_pct:     number;
  acceptable_pct: number;
  weak_pct:       number;
  risky_pct:      number;
  avg_xp:         number;
};

type LessonStats = {
  lesson_id:       string;
  lesson_title:    string;
  total_completions: number;
  avg_xp:          number;
  scenarios:       ScenarioStats[];
};

function AnalyticsTab({ worldId, lessons }: { worldId: string; lessons: Lesson[] }) {
  const [stats,   setStats]   = useState<LessonStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalLearners, setTotalLearners] = useState(0);
  const [totalAttempts, setTotalAttempts] = useState(0);

  useEffect(() => {
    if (!worldId) return;

    (async () => {
      setLoading(true);

      const [attemptsRes, completionsRes, scenariosRes] = await Promise.all([
        supabase
          .from('kp_user_scenario_attempts')
          .select('user_id, scenario_id, lesson_id, quality_tier, xp_awarded')
          .eq('world_id', worldId),
        supabase
          .from('kp_user_lesson_progress')
          .select('user_id, lesson_id, completed, xp_earned')
          .eq('world_id', worldId)
          .eq('completed', true),
        supabase
          .from('kp_scenarios')
          .select('id, lesson_id, title')
          .eq('world_id', worldId),
      ]);

      const attempts    = attemptsRes.data    ?? [];
      const completions = completionsRes.data ?? [];
      const scenarios   = scenariosRes.data   ?? [];

      const uniqueLearners = new Set(attempts.map((a) => a.user_id));
      setTotalLearners(uniqueLearners.size);
      setTotalAttempts(attempts.length);

      const scenarioMap = new Map<string, string>();
      for (const sc of scenarios) scenarioMap.set(sc.id, sc.title);

      const lessonMap = new Map<string, string>();
      for (const l of lessons) lessonMap.set(l.id, l.title);

      const completionsByLesson = new Map<string, { count: number; xpSum: number }>();
      for (const c of completions) {
        const prev = completionsByLesson.get(c.lesson_id) ?? { count: 0, xpSum: 0 };
        completionsByLesson.set(c.lesson_id, {
          count:  prev.count + 1,
          xpSum:  prev.xpSum + (c.xp_earned ?? 0),
        });
      }

      const attemptsByScenario = new Map<string, { lesson_id: string; tiers: string[]; xps: number[] }>();
      for (const a of attempts) {
        const prev: { lesson_id: string; tiers: string[]; xps: number[] } = attemptsByScenario.get(a.scenario_id) ?? { lesson_id: a.lesson_id, tiers: [], xps: [] };
        prev.tiers.push(a.quality_tier);
        prev.xps.push(a.xp_awarded ?? 0);
        attemptsByScenario.set(a.scenario_id, prev);
      }

      const lessonIds = lessons.map((l) => l.id);
      const lessonStatsArr: LessonStats[] = lessonIds.map((lid) => {
        const lComp = completionsByLesson.get(lid);
        const lScenarios: ScenarioStats[] = scenarios
          .filter((sc) => sc.lesson_id === lid)
          .map((sc) => {
            const data = attemptsByScenario.get(sc.id);
            if (!data || data.tiers.length === 0) {
              return {
                scenario_id:    sc.id,
                scenario_title: sc.title,
                lesson_title:   lessonMap.get(lid) ?? '',
                total_attempts: 0,
                strong_pct:     0,
                acceptable_pct: 0,
                weak_pct:       0,
                risky_pct:      0,
                avg_xp:         0,
              };
            }
            const total = data.tiers.length;
            const count = (tier: string) => data.tiers.filter((t) => t === tier).length;
            const pct   = (n: number) => Math.round((n / total) * 100);
            const xpAvg = Math.round(data.xps.reduce((a, b) => a + b, 0) / total);
            return {
              scenario_id:    sc.id,
              scenario_title: sc.title,
              lesson_title:   lessonMap.get(lid) ?? '',
              total_attempts: total,
              strong_pct:     pct(count('strong')),
              acceptable_pct: pct(count('acceptable')),
              weak_pct:       pct(count('weak')),
              risky_pct:      pct(count('risky')),
              avg_xp:         xpAvg,
            };
          });

        return {
          lesson_id:         lid,
          lesson_title:      lessonMap.get(lid) ?? '',
          total_completions: lComp?.count ?? 0,
          avg_xp:            lComp ? Math.round(lComp.xpSum / lComp.count) : 0,
          scenarios:         lScenarios,
        };
      }).filter((ls) => ls.scenarios.length > 0);

      setStats(lessonStatsArr);
      setLoading(false);
    })();
  }, [worldId, lessons]);

  if (loading) {
    return (
      <View style={s.analyticsCenter}>
        <ActivityIndicator color={PRIMARY} />
        <Text style={[s.analyticsNote, { marginTop: 12 }]}>טוען נתונים...</Text>
      </View>
    );
  }

  if (totalAttempts === 0) {
    return (
      <View style={s.analyticsCenter}>
        <Text style={s.analyticsEmptyIcon}>📊</Text>
        <Text style={s.analyticsEmptyTitle}>אין נתונים עדיין</Text>
        <Text style={s.analyticsNote}>נתוני ביצועים יופיעו לאחר שלומדים ישלימו תרחישים</Text>
      </View>
    );
  }

  return (
    <View style={s.analyticsRoot}>
      {/* Summary row */}
      <View style={s.analyticsSummaryRow}>
        <View style={s.analyticsSumCard}>
          <Text style={s.analyticsSumValue}>{totalLearners}</Text>
          <Text style={s.analyticsSumLabel}>לומדים פעילים</Text>
        </View>
        <View style={s.analyticsSumCard}>
          <Text style={s.analyticsSumValue}>{totalAttempts}</Text>
          <Text style={s.analyticsSumLabel}>ניסיונות תרחישים</Text>
        </View>
        <View style={s.analyticsSumCard}>
          <Text style={s.analyticsSumValue}>{stats.reduce((s, l) => s + l.total_completions, 0)}</Text>
          <Text style={s.analyticsSumLabel}>השלמות שיעורים</Text>
        </View>
      </View>

      {/* Per-lesson breakdown */}
      {stats.map((ls) => (
        <View key={ls.lesson_id} style={s.analyticsLessonCard}>
          <View style={s.analyticsLessonHeader}>
            <Text style={s.analyticsLessonTitle}>{ls.lesson_title}</Text>
            <View style={s.analyticsLessonMeta}>
              {ls.total_completions > 0 && (
                <Text style={s.analyticsMetaBadge}>{ls.total_completions} השלמות</Text>
              )}
              {ls.avg_xp > 0 && (
                <Text style={[s.analyticsMetaBadge, { color: GOLD }]}>⚡ {ls.avg_xp} XP ממוצע</Text>
              )}
            </View>
          </View>

          {ls.scenarios.map((sc) => (
            <ScenarioAnalyticsRow key={sc.scenario_id} sc={sc} />
          ))}
        </View>
      ))}
    </View>
  );
}

function ScenarioAnalyticsRow({ sc }: { sc: ScenarioStats }) {
  if (sc.total_attempts === 0) {
    return (
      <View style={s.scRow}>
        <Text style={s.scTitle} numberOfLines={2}>{sc.scenario_title}</Text>
        <Text style={s.scEmpty}>אין ניסיונות עדיין</Text>
      </View>
    );
  }

  const tiers: Array<{ key: string; label: string; pct: number; color: string }> = [
    { key: 'strong',     label: 'חזק',    pct: sc.strong_pct,     color: GREEN   },
    { key: 'acceptable', label: 'סביר',   pct: sc.acceptable_pct, color: GOLD    },
    { key: 'weak',       label: 'חלש',    pct: sc.weak_pct,       color: ORANGE  },
    { key: 'risky',      label: 'מסוכן',  pct: sc.risky_pct,      color: RED     },
  ];

  return (
    <View style={s.scRow}>
      <View style={s.scTitleRow}>
        <Text style={s.scTitle} numberOfLines={2}>{sc.scenario_title}</Text>
        <Text style={s.scAttempts}>{sc.total_attempts} ניסיונות</Text>
      </View>

      {/* Stacked bar */}
      <View style={s.scBar}>
        {tiers.filter((t) => t.pct > 0).map((t) => (
          <View
            key={t.key}
            style={[s.scBarSegment, { width: `${t.pct}%` as any, backgroundColor: t.color }]}
          />
        ))}
      </View>

      {/* Legend */}
      <View style={s.scLegend}>
        {tiers.filter((t) => t.pct > 0).map((t) => (
          <View key={t.key} style={s.scLegendItem}>
            <View style={[s.scLegendDot, { backgroundColor: t.color }]} />
            <Text style={s.scLegendText}>{t.label} {t.pct}%</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

// ── SettingsTab ───────────────────────────────────────────────────────────────

function SettingsTab({
  world,
  lessons,
  onPublish,
  onArchive,
}: {
  world: WorldMeta;
  lessons: Lesson[];
  onPublish: () => Promise<void>;
  onArchive: () => Promise<void>;
}) {
  const [publishLoading, setPublishLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [previewValidated, setPreviewValidated] = useState(false);
  const [approvalConfirmed, setApprovalConfirmed] = useState(false);
  const isPublished = world.status === 'published';
  const textForConsistencyCheck = [
    world.world_name,
    world.description ?? '',
    world.learning_goal ?? '',
    world.narrative_intro ?? '',
    ...lessons.flatMap((lesson) => [lesson.title, lesson.description ?? '']),
  ].join(' ');
  const latinChars = (textForConsistencyCheck.match(/[A-Za-z]/g) ?? []).length;
  const hebrewChars = (textForConsistencyCheck.match(/[\u0590-\u05FF]/g) ?? []).length;
  const dominantChars = Math.max(latinChars, hebrewChars);
  const minorChars = Math.min(latinChars, hebrewChars);
  const languageConsistencyRatio = dominantChars > 0 ? minorChars / dominantChars : 0;

  const publishChecks = useMemo<PublishGateCheck[]>(() => {
    const lessonsWithDescription = lessons.filter((lesson) => (lesson.description ?? '').trim().length >= 20).length;
    const lessonsWithDuration = lessons.filter((lesson) => Number(lesson.estimated_minutes ?? 0) > 0).length;
    const lessonsWithTitle = lessons.filter((lesson) => lesson.title.trim().length >= 5).length;
    const practiceEnabledLessons = lessons.filter((lesson) => Number(lesson.xp_reward ?? 0) > 0).length;
    const learningGoalReady = (world.learning_goal ?? '').trim().length >= 18;
    const descriptionReady = (world.description ?? '').trim().length >= 40;
    const clarityReady = lessons.every((lesson) => lesson.title.trim().length >= 5 && (lesson.description ?? '').trim().length >= 20);

    return [
      {
        id: 'content-completeness',
        title: 'שלמות תוכן',
        detail: `לפחות 2 שיעורים מלאים עם תיאור, משך ומבנה ברור (${lessonsWithDescription}/${Math.max(lessons.length, 1)} עם תיאור איכותי).`,
        passed: lessons.length >= 2 && lessonsWithDescription === lessons.length && lessonsWithDuration === lessons.length,
      },
      {
        id: 'lesson-quality-baseline',
        title: 'איכות שיעורים בסיסית',
        detail: `אין כותרות או תיאורים ריקים (${lessonsWithTitle}/${Math.max(lessons.length, 1)} כותרות תקינות).`,
        passed: lessons.length > 0 && lessonsWithTitle === lessons.length && lessonsWithDescription === lessons.length,
      },
      {
        id: 'practice-coverage',
        title: 'כיסוי תרגול לפני פרסום',
        detail: `לפחות שיעור אחד עם תרגול פעיל (XP חיובי) כדי שהלומד יוכל להיכנס לשלב Practice.`,
        passed: practiceEnabledLessons >= 1,
      },
      {
        id: 'language-consistency',
        title: 'עקביות שפה',
        detail: 'התוכן משתמש בשפה אחת עקבית כמעט לחלוטין, ללא ערבוב מבלבל ללומד.',
        passed: languageConsistencyRatio <= 0.15,
      },
      {
        id: 'learner-clarity',
        title: 'בהירות ללומד',
        detail: 'מטרת הלמידה והתיאור ברורים, וכל שיעור מציג מה הלומד יקבל.',
        passed: learningGoalReady && descriptionReady && clarityReady,
      },
      {
        id: 'publish-readiness',
        title: 'מוכנות לפרסום',
        detail: 'בוצעה בדיקת תצוגה כלומד ואישור יוצר שהמסלול אמין, מדויק ומוכן לפרסום.',
        passed: previewValidated && approvalConfirmed,
      },
    ];
  }, [approvalConfirmed, languageConsistencyRatio, lessons, previewValidated, world.description, world.learning_goal]);

  const allChecksPassed = publishChecks.every((check) => check.passed);

  const handlePublish = async () => {
    if (!allChecksPassed) return;
    setPublishLoading(true);
    await onPublish();
    setPublishLoading(false);
  };

  const handleArchive = async () => {
    setArchiveLoading(true);
    await onArchive();
    setArchiveLoading(false);
  };

  return (
    <View style={s.section}>
      <Text style={s.sectionTitle}>הגדרות מסלול</Text>

      <View style={s.statsGrid}>
        <StatBox label="שיעורים" value={String(lessons.length)} />
        <StatBox label="סטטוס" value={isPublished ? 'Published' : world.status === 'archived' ? 'Hidden' : 'Draft'} />
        <StatBox label="נוצר" value={new Date(world.created_at).toLocaleDateString('he-IL')} />
        {world.published_at && <StatBox label="פורסם" value={new Date(world.published_at).toLocaleDateString('he-IL')} />}
      </View>

      {!isPublished && (
        <View style={s.reviewCard}>
          <Text style={s.reviewCardTitle}>שער סקירה ואישור לפני פרסום</Text>
          <Text style={s.reviewCardSub}>
            כדי לפרסם, צריך לעבור על צ׳קליסט האיכות ולאשר שהמסלול מוכן ללומדים. הפרסום ננעל עד שכל השלבים הושלמו.
          </Text>

          <View style={s.checklist}>
            {publishChecks.map((check) => (
              <View key={check.id} style={[s.checkItem, check.passed ? s.checkItemPassed : s.checkItemFailed]}>
                <Text style={[s.checkStatus, check.passed ? s.checkStatusPassed : s.checkStatusFailed]}>
                  {check.passed ? '✓' : '•'}
                </Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.checkTitle}>{check.title}</Text>
                  <Text style={s.checkDetail}>{check.detail}</Text>
                </View>
              </View>
            ))}
          </View>

          <Pressable
            style={s.previewValidationBtn}
            onPress={() => {
              router.push(`/learn/path/${world.slug}` as any);
              setPreviewValidated(true);
            }}
            accessibilityRole="button"
          >
            <ExternalLink color={TEXT} size={15} />
            <Text style={s.previewValidationText}>
              {previewValidated ? 'תצוגת לומד נבדקה ✓' : 'בדיקת תצוגת לומד לפני פרסום'}
            </Text>
          </Pressable>

          <Pressable
            style={[s.confirmationRow, approvalConfirmed && s.confirmationRowActive]}
            onPress={() => setApprovalConfirmed((prev) => !prev)}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: approvalConfirmed }}
          >
            <Text style={[s.confirmationCheck, approvalConfirmed && s.confirmationCheckActive]}>{approvalConfirmed ? '✓' : ''}</Text>
            <Text style={s.confirmationText}>
              אני מאשר/ת שהתוכן שלם, עקבי, ברור ללומד ומוכן לפרסום לציבור.
            </Text>
          </Pressable>
        </View>
      )}

      {isPublished && (
        <Pressable style={s.viewLearnerBtn} onPress={() => router.push(`/learn/path/${world.slug}` as any)} accessibilityRole="link">
          <ExternalLink color={theme.colors.textLight} size={15} />
          <Text style={s.viewLearnerBtnText}>פתח כלומד</Text>
        </Pressable>
      )}

      {!isPublished && (
        <Pressable
          style={[s.publishBtn, !allChecksPassed && s.publishBtnDisabled]}
          onPress={handlePublish}
          disabled={publishLoading || !allChecksPassed}
          accessibilityRole="button"
        >
          {publishLoading
            ? <ActivityIndicator size="small" color="#fff" />
            : <Text style={s.publishBtnText}>{allChecksPassed ? 'אשר ופרסם מסלול →' : 'השלם סקירה לפני פרסום'}</Text>}
        </Pressable>
      )}

      {world.status !== 'archived' && (
        <Pressable style={s.archiveBtn} onPress={handleArchive} disabled={archiveLoading} accessibilityRole="button">
          {archiveLoading ? <ActivityIndicator size="small" color={TEXT_MED} /> : <Text style={s.archiveBtnText}>העבר לארכיב</Text>}
        </Pressable>
      )}
    </View>
  );
}

// ── Helper components ─────────────────────────────────────────────────────────

function FieldGroup({ label, children, style }: { label: string; children: React.ReactNode; style?: object }) {
  return (
    <View style={[{ marginBottom: 12 }, style]}>
      <Text style={s.fieldLabel}>{label}</Text>
      {children}
    </View>
  );
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <View style={s.statBox}>
      <Text style={s.statBoxValue}>{value}</Text>
      <Text style={s.statBoxLabel}>{label}</Text>
    </View>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  root:             { flex: 1, backgroundColor: BG },
  center:           { flex: 1, backgroundColor: BG, alignItems: 'center', justifyContent: 'center', gap: 16 },
  loadingText:      { fontFamily: theme.fonts.regular, color: TEXT_MED, fontSize: 14 },
  errorText:        { fontFamily: theme.fonts.medium, color: RED, fontSize: 15 },
  backBtn:          { paddingHorizontal: 20, paddingVertical: 10, backgroundColor: SURFACE, borderRadius: 8 },
  backBtnText:      { fontFamily: theme.fonts.medium, color: TEXT, fontSize: 14 },

  topBar: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    paddingTop: Platform.OS === 'web' ? 24 : 52,
    paddingBottom: 12,
    paddingHorizontal: 16,
    backgroundColor: SURFACE,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    gap: 12,
  },
  backIcon:       { padding: 6 },
  topTitle:       { fontFamily: theme.fonts.bold, fontSize: 17, color: TEXT, textAlign: 'right' },
  topSub:         { fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MED, textAlign: 'right' },
  v2Badge:        { borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 },
  v2BadgeText:    { fontFamily: theme.fonts.bold, fontSize: 11 },

  tabBar: {
    flexDirection: 'row-reverse',
    backgroundColor: SURFACE,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    paddingHorizontal: 8,
  },
  tabBtn: { flex: 1, alignItems: 'center', paddingVertical: 12, borderBottomWidth: 2, borderBottomColor: 'transparent' },
  tabBtnActive: { borderBottomColor: PRIMARY },
  tabBtnText: { fontFamily: theme.fonts.medium, fontSize: 13, color: TEXT_MED },
  tabBtnTextActive: { color: PRIMARY },

  scroll:         { flex: 1 },
  scrollContent:  { paddingBottom: 60 },

  section: {
    maxWidth: 700,
    alignSelf: 'center' as any,
    width: '100%',
    padding: 20,
    gap: 4,
  },
  sectionTitle:    { fontFamily: theme.fonts.bold, fontSize: 18, color: TEXT, textAlign: 'right', marginBottom: 16 },
  subSectionTitle: { fontFamily: theme.fonts.semiBold, fontSize: 14, color: TEXT_MED, textAlign: 'right', marginTop: 12, marginBottom: 8 },
  emptyText:       { fontFamily: theme.fonts.regular, color: TEXT_MED, textAlign: 'right', fontSize: 14, marginTop: 24 },

  fieldLabel:      { fontFamily: theme.fonts.medium, fontSize: 13, color: TEXT_MED, textAlign: 'right', marginBottom: 6 },
  input: {
    backgroundColor: SURFACE2,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontFamily: theme.fonts.regular,
    fontSize: 14,
    color: TEXT,
    textAlign: 'right',
  },
  inputMulti:      { minHeight: 70, paddingTop: 10 },

  row:             { flexDirection: 'row-reverse', gap: 10 },

  langRow:         { flexDirection: 'row-reverse', gap: 8, flexWrap: 'wrap' },
  langChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: SURFACE2,
  },
  langChipText:    { fontFamily: theme.fonts.medium, fontSize: 13, color: TEXT },

  colorRow:        { flexDirection: 'row-reverse', gap: 10, flexWrap: 'wrap' },
  colorSwatch:     { width: 32, height: 32, borderRadius: 16, borderWidth: 2, borderColor: 'transparent' },
  colorSwatchActive: { borderColor: '#fff' },

  copyLinkBtn: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginTop: 8,
  },
  copyLinkText:    { fontFamily: theme.fonts.medium, fontSize: 13, color: TEXT_MED },

  saveBtn: {
    backgroundColor: PRIMARY,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    marginTop: 16,
  },
  saveBtnSmall: {
    backgroundColor: PRIMARY,
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: 'center',
    marginTop: 12,
  },
  saveBtnText:     { fontFamily: theme.fonts.bold, fontSize: 15, color: '#fff' },

  lessonCard: {
    backgroundColor: SURFACE,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BORDER,
    marginBottom: 10,
    overflow: 'hidden',
  },
  lessonCardHeader: { flexDirection: 'row-reverse', alignItems: 'center', padding: 14, gap: 10 },
  lessonOrderBadge: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: `${PRIMARY}22`,
    alignItems: 'center',
    justifyContent: 'center',
  },
  lessonOrderText:  { fontFamily: theme.fonts.bold, fontSize: 12, color: PRIMARY },
  lessonCardTitle:  { flex: 1, fontFamily: theme.fonts.semiBold, fontSize: 14, color: TEXT, textAlign: 'right' },
  lessonChevron:    { fontFamily: theme.fonts.regular, color: TEXT_MED, fontSize: 12 },
  lessonCardBody:   { padding: 14, borderTopWidth: 1, borderTopColor: BORDER, gap: 4 },

  lessonSelector:   { marginBottom: 16 },
  lessonChip: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: SURFACE,
    maxWidth: 180,
  },
  lessonChipActive: { borderColor: PRIMARY, backgroundColor: `${PRIMARY}22` },
  lessonChipText:   { fontFamily: theme.fonts.medium, fontSize: 13, color: TEXT_MED },
  lessonChipTextActive: { color: PRIMARY },

  scenarioCard: {
    backgroundColor: SURFACE,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BORDER,
    marginBottom: 10,
    overflow: 'hidden',
  },
  scenarioHeader:   { flexDirection: 'row-reverse', alignItems: 'center', padding: 14, gap: 10 },
  scenarioTitle:    { fontFamily: theme.fonts.semiBold, fontSize: 14, color: TEXT, textAlign: 'right' },
  scenarioMeta:     { fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MED, textAlign: 'right', marginTop: 2 },
  scenarioBody:     { padding: 14, borderTopWidth: 1, borderTopColor: BORDER, gap: 4 },

  actionCard: {
    backgroundColor: SURFACE2,
    borderRadius: 10,
    padding: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: BORDER,
  },
  actionHeader:     { flexDirection: 'row-reverse', alignItems: 'center', gap: 8, marginBottom: 8 },
  actionIndex:      { fontFamily: theme.fonts.medium, fontSize: 12, color: TEXT_MED },
  qualityBadge:     { borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 },
  qualityBadgeText: { fontFamily: theme.fonts.bold, fontSize: 11 },

  statsGrid: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 10, marginBottom: 16 },
  statBox: {
    flex: 1,
    minWidth: 100,
    backgroundColor: SURFACE,
    borderRadius: 10,
    padding: 14,
    borderWidth: 1,
    borderColor: BORDER,
    alignItems: 'center',
  },
  statBoxValue:     { fontFamily: theme.fonts.bold, fontSize: 18, color: TEXT },
  statBoxLabel:     { fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MED, marginTop: 4 },

  publishBtn: {
    backgroundColor: GREEN,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    marginBottom: 10,
  },
  publishBtnDisabled: {
    backgroundColor: '#2A2A3C',
  },
  publishBtnText:   { fontFamily: theme.fonts.bold, fontSize: 15, color: '#fff' },
  reviewCard: {
    backgroundColor: SURFACE,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
    gap: 10,
  },
  reviewCardTitle: { fontFamily: theme.fonts.bold, fontSize: 15, color: TEXT, textAlign: 'right' },
  reviewCardSub: { fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT_MED, textAlign: 'right', lineHeight: 20 },
  checklist: { gap: 8 },
  checkItem: {
    borderRadius: 10,
    borderWidth: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    gap: 10,
  },
  checkItemPassed: {
    borderColor: `${GREEN}88`,
    backgroundColor: `${GREEN}12`,
  },
  checkItemFailed: {
    borderColor: BORDER,
    backgroundColor: SURFACE2,
  },
  checkStatus: {
    width: 18,
    height: 18,
    borderRadius: 9,
    textAlign: 'center',
    fontFamily: theme.fonts.bold,
    fontSize: 12,
    overflow: 'hidden',
    lineHeight: 18,
  },
  checkStatusPassed: {
    color: GREEN,
    backgroundColor: `${GREEN}22`,
  },
  checkStatusFailed: {
    color: TEXT_MED,
    backgroundColor: '#2A2846',
  },
  checkTitle: { fontFamily: theme.fonts.semiBold, fontSize: 13, color: TEXT, textAlign: 'right' },
  checkDetail: { fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MED, textAlign: 'right', marginTop: 2, lineHeight: 18 },
  previewValidationBtn: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: SURFACE2,
    paddingVertical: 11,
  },
  previewValidationText: { fontFamily: theme.fonts.medium, fontSize: 13, color: TEXT },
  confirmationRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: BORDER,
    paddingVertical: 10,
    paddingHorizontal: 12,
    gap: 10,
    backgroundColor: SURFACE2,
  },
  confirmationRowActive: {
    borderColor: `${GREEN}88`,
    backgroundColor: `${GREEN}12`,
  },
  confirmationCheck: {
    width: 20,
    height: 20,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: BORDER,
    color: 'transparent',
    textAlign: 'center',
    lineHeight: 18,
    fontFamily: theme.fonts.bold,
    backgroundColor: '#2A2846',
  },
  confirmationCheckActive: {
    color: '#fff',
    borderColor: GREEN,
    backgroundColor: GREEN,
  },
  confirmationText: {
    flex: 1,
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: TEXT,
    textAlign: 'right',
    lineHeight: 20,
  },
  archiveBtn: {
    backgroundColor: 'transparent',
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: BORDER,
  },
  archiveBtnText:   { fontFamily: theme.fonts.medium, fontSize: 14, color: TEXT_MED },
  viewLearnerBtn: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: BORDER,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginBottom: 10,
    alignSelf: 'flex-start' as any,
  },
  viewLearnerBtnText: { fontFamily: theme.fonts.medium, fontSize: 13, color: theme.colors.textLight },

  toast: {
    position: 'absolute' as any,
    bottom: 32,
    alignSelf: 'center' as any,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 24,
  },
  toastText: { fontFamily: theme.fonts.bold, fontSize: 14, color: '#fff' },

  analyticsRoot: { gap: 16 },
  analyticsCenter: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    gap: 12,
  },
  analyticsEmptyIcon: { fontSize: 40 },
  analyticsEmptyTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 17,
    color: TEXT,
    textAlign: 'center',
  },
  analyticsNote: {
    fontFamily: theme.fonts.regular,
    fontSize: 13,
    color: TEXT_MED,
    textAlign: 'center',
  },
  analyticsSummaryRow: {
    flexDirection: 'row-reverse',
    gap: 10,
  },
  analyticsSumCard: {
    flex: 1,
    backgroundColor: SURFACE,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BORDER,
    padding: 14,
    alignItems: 'center',
    gap: 4,
  },
  analyticsSumValue: {
    fontFamily: theme.fonts.bold,
    fontSize: 22,
    color: TEXT,
  },
  analyticsSumLabel: {
    fontFamily: theme.fonts.regular,
    fontSize: 11,
    color: TEXT_MED,
    textAlign: 'center',
  },
  analyticsLessonCard: {
    backgroundColor: SURFACE,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: BORDER,
    overflow: 'hidden',
  },
  analyticsLessonHeader: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    gap: 10,
  },
  analyticsLessonTitle: {
    fontFamily: theme.fonts.bold,
    fontSize: 15,
    color: TEXT,
    flex: 1,
    textAlign: 'right',
  },
  analyticsLessonMeta: {
    flexDirection: 'row-reverse',
    gap: 8,
    flexWrap: 'wrap',
  },
  analyticsMetaBadge: {
    fontFamily: theme.fonts.medium,
    fontSize: 12,
    color: TEXT_MED,
  },

  scRow: {
    padding: 14,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    gap: 10,
  },
  scTitleRow: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },
  scTitle: {
    fontFamily: theme.fonts.medium,
    fontSize: 13,
    color: TEXT,
    flex: 1,
    textAlign: 'right',
    lineHeight: 20,
  },
  scAttempts: {
    fontFamily: theme.fonts.regular,
    fontSize: 11,
    color: TEXT_MED,
    flexShrink: 0,
  },
  scEmpty: {
    fontFamily: theme.fonts.regular,
    fontSize: 12,
    color: TEXT_MED,
    textAlign: 'right',
  },
  scBar: {
    flexDirection: 'row-reverse',
    height: 8,
    borderRadius: 999,
    overflow: 'hidden',
    backgroundColor: SURFACE2,
  },
  scBarSegment: {
    height: '100%',
  },
  scLegend: {
    flexDirection: 'row-reverse',
    gap: 12,
    flexWrap: 'wrap',
  },
  scLegendItem: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 5,
  },
  scLegendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  scLegendText: {
    fontFamily: theme.fonts.regular,
    fontSize: 11,
    color: TEXT_MED,
  },
});
